import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { FileText, Download, Search, User } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Payslips = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [payslips, setPayslips] = useState([]);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [search, setSearch] = useState('');
  // Stage 73.20cf — payslip PDF template choice (classic | branded).
  // Persist in localStorage so admins keep their preferred layout per session.
  const [selectedPayslipTemplate, setSelectedPayslipTemplate] = useState(
    () => localStorage.getItem('payslip_template') || 'classic',
  );
  useEffect(() => { localStorage.setItem('payslip_template', selectedPayslipTemplate); },
            [selectedPayslipTemplate]);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      const list = Array.isArray(data) ? data : [];
      setCompanies(list);
      setSelectedCompany((cur) => (list.length > 0 && !cur ? list[0].id : cur));
    } catch (e) { setCompanies([]); console.error(e); }
  }, [setSelectedCompany]);

  const fetchPeriods = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/periods?company_id=${selectedCompany}`, { withCredentials: true });
      const list = Array.isArray(data) ? data : [];
      setPeriods(list);
      const released = list.find(p => ['released', 'locked', 'approved'].includes(p.status));
      if (released) setSelectedPeriod(released.id);
    } catch (e) { setPeriods([]); console.error(e); }
  }, [selectedCompany]);

  const fetchPayslips = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/payslips?period_id=${selectedPeriod}`, { withCredentials: true });
      setPayslips(Array.isArray(data) ? data : []);
    } catch (e) { setPayslips([]); console.error(e); }
  }, [selectedPeriod]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  useEffect(() => { if (selectedCompany) fetchPeriods(); }, [selectedCompany, fetchPeriods]);
  useEffect(() => { if (selectedPeriod) fetchPayslips(); }, [selectedPeriod, fetchPayslips]);

  const filtered = payslips.filter(p =>
    !search || p.employee_name?.toLowerCase().includes(search.toLowerCase()) || p.employee_code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainLayout>
      <div data-testid="payslips-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Payslips</h1>
            <p className="text-sm text-slate-500 mt-1">View and manage employee payslips</p>
          </div>
          <div className="flex items-center space-x-3">
            <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select data-testid="period-selector" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Select Period</option>{periods.map((p) => <option key={p.id} value={p.id}>{p.period_name}</option>)}
            </select>
            <button
              data-testid="payslips-export-btn"
              onClick={() => {
                if (filtered.length === 0) { toast.error('No payslips to export'); return; }
                downloadCSV(filtered.map(p => ({
                  Employee_Code: p.employee_code,
                  Employee_Name: p.employee_name,
                  Period: p.period_name,
                  Gross_Pay: p.gross_pay,
                  Total_Deductions: p.total_deductions,
                  Net_Pay: p.net_pay,
                  Total_Days: p.total_days,
                  Payable_Days: p.payable_days,
                  LOP_Days: p.lop_days,
                })), `payslips_${selectedPeriod}.csv`);
                toast.success('Payslips exported');
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-sm font-medium"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        <div className="relative"><Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input data-testid="payslip-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or code..." className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm" />
        </div>

        {/* Empty-state callout — payslips only exist after a payroll run is approved/released
            AND the "Generate Payslips" action is triggered on that run. Without this hint
            users assume the page is broken when they land here on a fresh tenant. */}
        {!selectedPayslip && periods.length === 0 && (
          <div data-testid="payslips-no-periods-callout" className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-amber-900 mb-1">No payroll periods yet</p>
            <p className="text-xs text-amber-800">
              Set up a Calendar &amp; Period under <span className="font-medium">Payroll Setup</span>, then process and approve a payroll run for that period.
            </p>
          </div>
        )}
        {!selectedPayslip && periods.length > 0 && !selectedPeriod && (
          <div data-testid="payslips-pick-period-callout" className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-blue-900 mb-1">Pick a period above</p>
            <p className="text-xs text-blue-800">
              Payslips appear once a payroll run reaches <span className="font-mono">approved</span> / <span className="font-mono">released</span> / <span className="font-mono">locked</span> status and the <span className="font-medium">Generate Payslips</span> action runs on that run (Payroll Run page).
            </p>
          </div>
        )}
        {!selectedPayslip && selectedPeriod && payslips.length === 0 && (
          <div data-testid="payslips-empty-period-callout" className="bg-slate-50 border border-slate-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-slate-800 mb-1">No payslips for this period</p>
            <p className="text-xs text-slate-600">
              The selected period has no generated payslips yet. Open <span className="font-medium">Payroll Run</span>, approve the run, and click <span className="font-medium">Generate Payslips</span>.
            </p>
          </div>
        )}

        {selectedPayslip ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{selectedPayslip.employee_name}</h2>
                <p className="text-sm text-slate-500">{selectedPayslip.employee_code} | {selectedPayslip.period_name}</p>
              </div>
              <button data-testid="back-to-list" onClick={() => setSelectedPayslip(null)} className="text-sm text-[#0052FF] hover:underline">Back to List</button>
            </div>

            {/* Stage 73.1 — Lead with Monthly Gross + Net Pay as the two hero
                numbers. Earlier this row was Total Days / Payable / LOP / Net.
                Indian payslip convention reads Gross → Deductions → Net → CTC,
                so Gross belongs first.  Days info moves into a thinner
                secondary strip below. */}
            <div className="grid grid-cols-2 gap-px bg-slate-100 border-b">
              <div className="bg-emerald-50 p-5 text-center">
                <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Monthly Gross</p>
                <p className="text-3xl font-bold text-emerald-800 font-mono mt-1" data-testid="payslip-gross-headline">
                  ₹{selectedPayslip.gross_pay?.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-blue-50 p-5 text-center">
                <p className="text-xs uppercase tracking-wide text-[#0052FF] font-semibold">Net Pay (take home)</p>
                <p className="text-3xl font-bold text-[#0052FF] font-mono mt-1" data-testid="payslip-net-headline">
                  ₹{selectedPayslip.net_pay?.toLocaleString('en-IN')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 p-3 bg-slate-50 border-b text-xs">
              <div className="text-center"><span className="text-slate-500">Total Days:</span> <span className="font-bold">{selectedPayslip.total_days}</span></div>
              <div className="text-center"><span className="text-slate-500">Payable:</span> <span className="font-bold">{selectedPayslip.payable_days}</span></div>
              <div className="text-center"><span className="text-slate-500">LOP:</span> <span className="font-bold text-red-500">{selectedPayslip.lop_days}</span></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-px bg-slate-100">
              <div className="bg-white p-5">
                <h3 className="text-sm font-semibold text-emerald-700 mb-3 uppercase tracking-wide">Earnings</h3>
                <div className="space-y-2">
                  {selectedPayslip.earnings?.map((e, i) => (
                    <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{e.head_name}</span><span className="font-mono font-medium">{e.amount?.toLocaleString()}</span></div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t border-slate-200"><span>Gross Pay</span><span className="font-mono">{selectedPayslip.gross_pay?.toLocaleString()}</span></div>
                </div>
              </div>
              <div className="bg-white p-5">
                <h3 className="text-sm font-semibold text-red-700 mb-3 uppercase tracking-wide">Deductions</h3>
                <div className="space-y-2">
                  {selectedPayslip.deductions?.map((d, i) => (
                    <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{d.head_name}</span><span className="font-mono font-medium text-red-600">{d.amount?.toLocaleString()}</span></div>
                  ))}
                  {selectedPayslip.loan_recovery > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">Loan Recovery</span><span className="font-mono text-red-600">{selectedPayslip.loan_recovery?.toLocaleString()}</span></div>}
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t border-slate-200"><span>Total Deductions</span><span className="font-mono text-red-600">{selectedPayslip.total_deductions?.toLocaleString()}</span></div>
                </div>
              </div>
            </div>

            {/* Stage 73.1 — Above-the-line CTC Summary
                Replaces the "Net Pay" footer (Net Pay is already the headline at top).
                This box shows: Monthly Gross + Employer PF + Employer ESI + Perks = Monthly CTC
                — matches the gross-based calc engine flow. */}
            <div className="p-5 bg-gradient-to-r from-indigo-50 to-blue-50 border-t">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-bold text-indigo-900">↑ Above-the-line CTC Summary</h3>
                  <p className="text-[10px] text-indigo-700">Total cost-to-company for this month (gross + employer share + perks)</p>
                </div>
                <div className="flex items-center gap-3">
                  <button data-testid="payslip-download-btn" onClick={(e) => {
                    e.stopPropagation();
                    downloadCSV([{
                      Employee: selectedPayslip.employee_name, Code: selectedPayslip.employee_code,
                      Period: selectedPayslip.period_name, Gross_Pay: selectedPayslip.gross_pay,
                      Total_Deductions: selectedPayslip.total_deductions, Net_Pay: selectedPayslip.net_pay,
                      Payable_Days: selectedPayslip.payable_days, LOP_Days: selectedPayslip.lop_days,
                    }], `payslip_${selectedPayslip.employee_code}_${selectedPayslip.period_name}.csv`);
                    toast.success('Payslip downloaded');
                  }} className="text-xs px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-md flex items-center gap-1">
                    <Download className="w-3 h-3" /> CSV
                  </button>
                  <button data-testid="payslip-pdf-btn" onClick={async (e) => {
                    e.stopPropagation();
                    const tpl = (selectedPayslipTemplate || 'classic');
                    try {
                      const response = await axios.get(
                        `${API_URL}/payroll/payslips/${selectedPayslip.employee_id}/${selectedPayslip.period_id}/pdf?template=${tpl}`,
                        { withCredentials: true, responseType: 'blob' }
                      );
                      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `payslip_${selectedPayslip.employee_code}_${selectedPayslip.period_name}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success(`PDF payslip downloaded (${tpl})`);
                    } catch {
                      toast.error('PDF generation failed');
                    }
                  }} className="text-xs px-3 py-1.5 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-md flex items-center gap-1">
                    <Download className="w-3 h-3" /> PDF
                  </button>
                  <select
                    data-testid="payslip-template-select"
                    value={selectedPayslipTemplate}
                    onChange={(e) => setSelectedPayslipTemplate(e.target.value)}
                    className="text-xs px-2 py-1.5 bg-white border-2 border-slate-200 rounded-md"
                    title="Payslip layout"
                  >
                    <option value="classic">Classic</option>
                    <option value="branded">Branded</option>
                  </select>
                </div>
              </div>
              <div className="bg-white rounded-md border border-indigo-200 p-3 space-y-1.5 text-sm" data-testid="payslip-ctc-summary">
                <div className="flex justify-between">
                  <span className="text-slate-700">Monthly Gross</span>
                  <span className="font-mono font-medium">₹{selectedPayslip.gross_pay?.toLocaleString('en-IN')}</span>
                </div>
                {(selectedPayslip.employer_contributions || []).map((ec, i) => (
                  <div key={i} className="flex justify-between text-slate-600">
                    <span>+ {ec.head_name || ec.name || ec.head_code}</span>
                    <span className="font-mono">₹{Number(ec.amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                ))}
                {Number(selectedPayslip.perks_monthly) > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>+ Other Perks (food, insurance, etc.)</span>
                    <span className="font-mono">₹{Number(selectedPayslip.perks_monthly).toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 mt-2 border-t border-indigo-200">
                  <span className="font-bold text-indigo-900">= Total Monthly CTC</span>
                  <span className="font-mono font-bold text-indigo-900 text-base" data-testid="payslip-monthly-ctc">
                    ₹{(
                      Number(selectedPayslip.gross_pay || 0)
                      + (selectedPayslip.employer_contributions || []).reduce((s, ec) => s + Number(ec.amount || 0), 0)
                      + Number(selectedPayslip.perks_monthly || 0)
                    ).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Employee</th>
                <th className="px-4 py-3 text-left font-medium">Code</th>
                <th className="px-4 py-3 text-left font-medium">Period</th>
                <th className="px-4 py-3 text-right font-medium">Gross</th>
                <th className="px-4 py-3 text-right font-medium">Deductions</th>
                <th className="px-4 py-3 text-right font-medium">Net Pay</th>
                <th className="px-4 py-3 text-left font-medium">Action</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((p, i) => (
                  <tr key={i} data-testid={`payslip-row-${i}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedPayslip(p)}>
                    <td className="px-4 py-3 text-slate-800">{p.employee_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{p.employee_code}</td>
                    <td className="px-4 py-3 text-slate-600">{p.period_name}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.gross_pay?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono text-red-600">{p.total_deductions?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-[#0052FF]">{p.net_pay?.toLocaleString()}</td>
                    <td className="px-4 py-3"><button className="text-xs text-[#0052FF] hover:underline">View</button></td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">{payslips.length === 0 ? 'No payslips for this period' : 'No matching results'}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Payslips;
